import React, { useState, useCallback, useSyncExternalStore } from 'react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import OsiriXIcon from './OsiriX'
import RadiAntIcon from './RadiAnt'

const isMac = /Mac/i.test(navigator.userAgent)
const isWindows = /Win/i.test(navigator.userAgent)

const HANDLER_DOWNLOAD_URL = '/downloads/corridor-handler.exe'

const checkCorridorInstalled = () => {
  try {
    return localStorage.getItem('corridor-handler-installed') === 'true'
  } catch { return false }
}

let listeners = []
const subscribe = (cb) => { listeners.push(cb); return () => { listeners = listeners.filter(l => l !== cb) } }
const notifyInstalled = () => { localStorage.setItem('corridor-handler-installed', 'true'); listeners.forEach(cb => cb()) }

const DicomViewerButton = ({ studyId, disabled }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const installed = useSyncExternalStore(subscribe, checkCorridorInstalled)

  const handleWindowsClick = useCallback((e) => {
    if (!e.shiftKey && checkCorridorInstalled()) {
      window.location.href = `corridor://${studyId}`
    } else {
      localStorage.removeItem('corridor-handler-installed')
      listeners.forEach(cb => cb())
      const a = document.createElement('a')
      a.href = HANDLER_DOWNLOAD_URL
      a.download = 'corridor-handler.exe'
      a.click()
      setDialogOpen(true)
    }
  }, [studyId])

  const handleInstalled = () => {
    notifyInstalled()
    setDialogOpen(false)
  }

  if (isMac) {
    return (
      <Tooltip title="OsiriX">
        <span>
          <IconButton
            aria-label="osirix"
            component="a"
            disabled={disabled}
            href={`osirix://?methodName=downloadURL&URL='https://api.pacs.corridor.vet/studies/${studyId}/zip'`}
          >
            <OsiriXIcon />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  if (isWindows) {
    return (
      <>
        <Tooltip title={installed ? 'Open in RadiAnt' : 'Install RadiAnt Handler'}>
          <span>
            <IconButton
              aria-label="radiant"
              disabled={disabled}
              onClick={handleWindowsClick}
            >
              <RadiAntIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm">
          <DialogTitle>Install Corridor Handler</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Double-click <strong>corridor-handler.exe</strong> to install.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Windows Defender may show a warning:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              1. Click <strong>"More info"</strong><br />
              2. Click <strong>"Run anyway"</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You only need to do this once.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleInstalled} variant="contained">I've Installed It</Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  return null
}

export default DicomViewerButton
